import type {Location} from "@/api/apiClient";

const MARKER_HEIGHT_PX = 50; // Approximate marker height in pixels
const OVERLAP_THRESHOLD_PX = 60; // Minimum vertical distance to avoid overlap

/**
 * Detects if pickup and dropoff markers would overlap at the current zoom level.
 * Returns true when markers are close enough vertically that they need to be flipped.
 */
export function useMarkersOverlap(
    map: google.maps.Map | null,
    pickup: Location | undefined,
    dropoff: Location | undefined,
    zoom: number
): boolean {
    if (!map || !pickup?.lat || !pickup?.lon || !dropoff?.lat || !dropoff?.lon) return false;

    const projection = map.getProjection();
    if (!projection) return false;

    const scale = Math.pow(2, zoom);

    const pickupWorld = projection.fromLatLngToPoint(
        new google.maps.LatLng(pickup.lat, pickup.lon)
    );
    const dropoffWorld = projection.fromLatLngToPoint(
        new google.maps.LatLng(dropoff.lat, dropoff.lon)
    );

    if (!pickupWorld || !dropoffWorld) return false;

    // Calculate pixel distance
    const deltaXPx = Math.abs(pickupWorld.x - dropoffWorld.x) * scale;
    const deltaYPx = Math.abs(pickupWorld.y - dropoffWorld.y) * scale;

    // Markers overlap if they're horizontally close and vertically close
    const horizontalOverlap = deltaXPx < 120; // Less than marker width
    const verticalOverlap = deltaYPx < MARKER_HEIGHT_PX + OVERLAP_THRESHOLD_PX;

    return horizontalOverlap && verticalOverlap;
}
